"use client";

import { useState } from "react";
import Link from "next/link";
import { Eye, EyeOff } from "lucide-react";
import AuthPanel, { useAuthPanelConfig } from "@/components/auth/AuthPanel";
import { Button } from "@/components/ui/Button";
import BrandLogo from "@/components/tenant/BrandLogo";
import { useTenant, useTenantAuthConfig } from "@/components/tenant/TenantProvider";
import { useTurnstile, isTurnstileError } from "@/hooks/use-turnstile";
import { getAttribution } from "@/lib/tracking/attribution";
import { identify } from "@/lib/tracking/client";
import { inputClassUser as inputClass } from "@/lib/form-classes";

/** Opsi dropdown "Tau dari mana?" default — netral, di-override auth.referral_sources. */
const DEFAULT_REFERRAL_SOURCES = [
  "Instagram",
  "TikTok",
  "YouTube",
  "Google / Search",
  "Dari teman",
  "Komunitas WhatsApp/Telegram",
  "Lainnya",
];

export default function RegisterPage() {
  const { brand_name } = useTenant();
  const auth = useTenantAuthConfig();
  // Field panel tidak diteruskan useTenantAuthConfig() → baca window.__TENANT__ langsung.
  const panel = useAuthPanelConfig();
  const hasPanel = panel.style !== "none";
  const referralSources =
    auth.referral_sources && auth.referral_sources.length > 0
      ? auth.referral_sources
      : DEFAULT_REFERRAL_SOURCES;
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", referral_source: "", _hp_website: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const turnstile = useTurnstile("turnstile-spin-v2");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateField, setDuplicateField] = useState<"email" | "phone" | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (isDuplicate) {
      setIsDuplicate(false);
      setDuplicateField(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsDuplicate(false);
    setDuplicateField(null);

    if (form.password !== confirmPassword) {
      setError("Password tidak cocok");
      return;
    }

    setLoading(true);

    try {
      const inviteCode =
        typeof window !== "undefined"
          ? new URLSearchParams(window.location.search).get("invite")?.trim() || undefined
          : undefined;
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...form,
          invite_code: inviteCode,
          cfTurnstileResponse: turnstile.token,
          affiliate_code: document.cookie.split(';').find(c => c.trim().startsWith('affiliate_code='))?.split('=')[1]?.trim() || undefined,
          attribution: getAttribution(),
        }),
      });

      const data = await res.json();
      if (!data.success) {
        // Token Turnstile SEKALI PAKAI dan sudah dikonsumsi server sebelum cek
        // duplikat — tanpa reset, submit ulang setelah 409 pasti ditolak.
        if (isTurnstileError(data.errorType)) {
          turnstile.noteServerRejection(data.errorType);
          setError(data.error);
        } else {
          turnstile.reset();
          if (res.status === 409) {
            setIsDuplicate(true);
            setDuplicateField((data.error as string)?.includes("WhatsApp") ? "phone" : "email");
          } else {
            setError(data.error || "Registrasi gagal");
          }
        }
        return;
      }

      if (data.autoVerified) {
        identify();
        window.location.href = "/dashboard";
        return;
      }
      window.location.href = `/verify-email?email=${encodeURIComponent(form.email)}`;
    } catch {
      turnstile.reset();
      setError("Terjadi kesalahan. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content" tabIndex={-1} className={hasPanel ? "min-h-screen grid lg:grid-cols-2" : "min-h-screen"}>
      {hasPanel && <AuthPanel variant={panel} isTyping={isTyping} />}

      {/* Form section — style 'none': satu kolom, form tetap center max-w-[420px] */}
      <div
        className={`flex items-center justify-center p-8 bg-[var(--surface)] overflow-y-auto ${
          hasPanel ? "" : "min-h-screen"
        }`}
      >
        <div className="w-full max-w-[420px] py-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <BrandLogo variant="header" priority />
          </div>

          <div className="text-center mb-8">
            <div className="hidden lg:flex justify-center mb-4">
              <BrandLogo variant="header" priority />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {auth.register_headline ?? `Daftar di ${brand_name}`}
            </h1>
            <p className="text-sm text-gray-500">
              {auth.register_subtitle ?? "Mulai perjalanan belajarmu hari ini"}
            </p>
          </div>

          {/* Duplicate account notice */}
          {isDuplicate ? (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-800 mb-0.5">Akun kamu sudah terdaftar</p>
              <p className="text-xs text-amber-700 mb-3">
                {duplicateField === "phone"
                  ? "Nomor WhatsApp ini sudah digunakan oleh akun lain."
                  : `Email ini sudah terdaftar di ${brand_name}.`}
              </p>
              <div className="flex gap-2">
                <Link
                  href="/login"
                  className="flex-1 text-center py-2 bg-[var(--brand-primary)] text-[var(--brand-on-primary,#fff)] text-xs font-semibold rounded-lg hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  Masuk
                </Link>
                <Link
                  href="/forgot-password"
                  className="flex-1 text-center py-2 border border-[var(--brand-primary)]/30 text-[var(--brand-primary)] text-xs font-semibold rounded-lg hover:bg-[var(--brand-primary-soft)] transition-colors"
                >
                  Lupa Kata Sandi
                </Link>
              </div>
            </div>
          ) : error ? (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {auth.registration_mode === "closed" && (
            <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              Pendaftaran siswa baru sedang ditutup untuk saat ini. Silakan hubungi admin {brand_name} jika kamu butuh akses.
            </div>
          )}
          {auth.registration_mode === "invite_only" && (
            <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              Pendaftaran hanya lewat undangan. Pastikan kamu membuka halaman ini dari link undangan resmi.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot field - hidden from users but bots will fill it */}
            <input type="text" name="_hp_website" value={form._hp_website || ''} onChange={handleChange} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="flex flex-col gap-1.5">
              <label htmlFor="name" className="text-sm font-medium text-gray-900">
                Nama Lengkap <span className="text-red-500">*</span>
              </label>
              <input
                id="name" name="name" type="text" placeholder="John Doe"
                autoComplete="name"
                value={form.name} onChange={handleChange} required
                onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="reg-email" className="text-sm font-medium text-gray-900">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="reg-email" name="email" type="email" placeholder="nama@email.com"
                autoComplete="email"
                value={form.email} onChange={handleChange} required
                onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="phone" className="text-sm font-medium text-gray-900">
                No. WhatsApp <span className="text-red-500">*</span>
              </label>
              <input
                id="phone" name="phone" type="tel" placeholder="08xxxxxxxxxx"
                autoComplete="tel"
                value={form.phone} onChange={handleChange} required
                onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="referral_source" className="text-sm font-medium text-gray-900">
                Tau {brand_name} dari mana?
              </label>
              <select
                id="referral_source" name="referral_source"
                value={form.referral_source}
                onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                className={inputClass}
              >
                <option value="">-- Pilih --</option>
                {referralSources.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="reg-password" className="text-sm font-medium text-gray-900">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="reg-password" name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Minimal 8 karakter"
                  autoComplete="new-password"
                  value={form.password} onChange={handleChange}
                  required minLength={8}
                  onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="confirm-password" className="text-sm font-medium text-gray-900">
                Ulangi Password <span className="text-red-500">*</span>
              </label>
              <input
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                placeholder="Ulangi password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                onFocus={() => setIsTyping(true)} onBlur={() => setIsTyping(false)}
                className={inputClass}
              />
              {confirmPassword && form.password !== confirmPassword && (
                <p className="text-xs text-red-500">Password tidak cocok</p>
              )}
            </div>

            <div className="my-2">{turnstile.widget}</div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={loading}
              className="w-full py-3 rounded-xl text-base mt-1"
            >
              {loading ? "Memproses..." : "Daftar Sekarang"}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8">
            Sudah punya akun?{" "}
            <Link href="/login" className="font-semibold text-gray-900 hover:text-[var(--brand-primary)]">
              Masuk
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
